import type { AuthProviderId } from "./auth.types";

import { Button } from "@heroui/react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "./AuthProvider";
import { safeAppPath } from "./returnPath";

type SignInState = { returnTo?: string };

export function SignInPage({ modal = false }: { modal?: boolean }) {
  const { providers, providersLoading, beginSignIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo =
    safeAppPath((location.state as SignInState | null)?.returnTo) ?? undefined;
  const content = (
    <section
      aria-labelledby="signin-title"
      aria-modal={modal || undefined}
      className="relative w-full max-w-lg rounded-3xl bg-background p-8 shadow-2xl sm:p-12"
      role={modal ? "dialog" : undefined}
    >
      {modal && (
        <Button
          aria-label="Close sign in"
          className="absolute right-5 top-5"
          variant="tertiary"
          onPress={() => navigate(-1)}
        >
          ×
        </Button>
      )}
      <p className="mb-3 text-center text-sm font-semibold uppercase tracking-widest text-accent">
        Welcome
      </p>
      <h1 className="text-center text-3xl font-semibold" id="signin-title">
        Log in or sign up
      </h1>
      <p className="mt-3 text-center text-muted">
        Continue securely with your social account.
      </p>
      <div className="mt-8 grid grid-cols-2 gap-3">
        {providers.map((provider) => (
          <Button
            key={provider.id}
            size="lg"
            variant="secondary"
            onPress={() => beginSignIn(provider.id as AuthProviderId, returnTo)}
          >
            Continue with {provider.displayName}
          </Button>
        ))}
        {providersLoading && (
          <p className="text-center text-sm">Loading sign-in options…</p>
        )}
        {!providersLoading && providers.length === 0 && (
          <p className="text-center text-sm text-danger">
            Sign-in providers are not configured.
          </p>
        )}
      </div>
    </section>
  );

  return modal ? (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/45 p-5">
      {content}
    </div>
  ) : (
    <main className="grid min-h-screen place-items-center bg-soft p-5">
      {content}
    </main>
  );
}
